import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {filter, map, skipUntil, switchMap} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {env} from '@/helper/env';
import {mc} from '@/core';
import {operationApi} from '@/api';
import {isLogin$} from '@/streams/auth';
import {browser$} from '@/streams/browser';

const dialogCache = getDialogStorage();

const dialog$ = new BehaviorSubject([]);
const dialogList$ = dialog$.pipe(
  filter((v) => v.length > 0),
  map(cleanTemplateOutDate),
  map(initTemplateCache)
);

export const dialogPageMount$ = new BehaviorSubject(false);
export const dialogStatus$ = new BehaviorSubject(false);
export const query$ = new Subject();
export const dialogInfo$ = combineLatest([dialogPageMount$, dialogList$, dialogStatus$]).pipe(map(getTemplateInRange), map(getFatigueIntervalTemplate), map(getHighestPriority), filter(Boolean));

combineLatest([query$.pipe(skipUntil(browser$)), isLogin$])
  .pipe(switchMap(([query, isLogin]) => (!isLogin ? [] : Promise.all([getDialogInfo(query), getTestDialogInfo(query)]))))
  .subscribe(([publishData = {}, testData]) => {
    if (testData && testData.rows) {
      dialog$.next([...convertData(publishData, 0), ...convertData(testData, 1)]);
      return;
    }
    dialog$.next(convertData(publishData, 0));
  });

async function getDialogInfo(query) {
  const {data, error} = await operationApi.getDialogDataList(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
    return {};
  }
}

async function getTestDialogInfo(query) {
  const {data, error} = await operationApi.getTestDialogDataList(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
    return {};
  }
}

function convertData(data, testing) {
  const {rows = []} = data;
  return rows.map((o) => {
    const {plan, config} = o;
    const {fatigueRule, priority, ...rest} = config || {};
    return {
      status: plan.status,
      platformList: plan.platformList,
      createTime: rest.createTime,
      startTime: plan.startTime,
      endTime: plan.endTime,
      platformAllPage: plan.platformRoute.web === 'ALL',
      platformWebRoutes: (plan.platformRoute.web && plan.platformRoute.web.split(',')) || [],
      fatigueType: fatigueRule.type === 'ONCE' ? 1 : 2,
      fatigueCount: fatigueRule.totalFatigue,
      fatigueIntervalSeconds: fatigueRule.fatigueInterval * 60000,
      currentCount: 0,
      priority: priority === 'LOW' ? -1 : priority === 'MID' ? 0 : 1,
      templateType: rest.templateType === 'DIALOG' ? 'image' : 'text',
      templateKey: `${!testing ? 't' : 'tt'}${rest.groupId}`,
      webPic: rest.webPic,
      appPic: rest.appPic,
      btn1: rest.btn1,
      btn2: rest.btn2,
      title: rest.title,
      content: rest.content,
      testing: !!testing,
    };
  });
}

function initTemplateCache(dataList) {
  if (dataList && dataList.length > 0) {
    dataList.reduce((p, o) => {
      if (!p.hasOwnProperty(o.templateKey)) {
        p[o.templateKey] = {count: 0, time: 0};
      }
      return p;
    }, dialogCache);
    return dataList;
  }
  return [];
}

function cleanTemplateOutDate(dataList) {
  const storageData = getDialogStorage();
  if (!dataList || dataList.length === 0) {
    return dataList;
  }
  const dataMap = dataList.reduce((p, o) => {
    p[o.templateKey] = true;
    return p;
  }, {});
  const cache = Object.keys(storageData).reduce((p, k) => {
    if (dataMap[k]) {
      p[k] = storageData[k];
    }
    return p;
  }, {});
  updateAllStorage(cache);
  return dataList;
}

function getFatigueIntervalTemplate([dataList, dialogStatus, milliseconds]) {
  const filterDataList = dataList.filter((item) => {
    const dialogCacheTemplate = dialogCache[item.templateKey];
    if (dialogCacheTemplate.count === 0) {
      return true;
    }
    const interval = milliseconds - dialogCacheTemplate.time - item.fatigueIntervalSeconds;
    return interval >= 0 && dialogCache.hasOwnProperty(item.templateKey) && dialogCacheTemplate.count < item.fatigueCount;
  });

  return [filterDataList, dialogStatus, milliseconds];
}

function getTemplateInRange([, filterList, dialogStatus]) {
  const currentMilliseconds = new Date().valueOf();
  const filterDataList = filterList.filter((item) => {
    if (item.testing) {
      return true;
    }
    return item.startTime <= currentMilliseconds && item.endTime >= currentMilliseconds;
  });

  reInitialTemplateFatigueCount(filterDataList, currentMilliseconds);
  return [filterDataList, dialogStatus, currentMilliseconds];
}

/**
 * count 写入localstorage
 * */
function getHighestPriority([filterList, dialogStatus, currentMilliseconds]) {
  if (filterList && filterList.length > 0 && !dialogStatus) {
    return [filterList, validatePath, updateStorageFun(currentMilliseconds)];
  }
  return [];
}

function validatePath(filterList, currentPath) {
  const availableContents = filterList.filter((content) => content.platformAllPage || isRootPage(currentPath, content.platformWebRoutes));
  if (availableContents.length <= 0) {
    return null;
  }
  const sortDataList = sortByPriority(availableContents);
  return sortDataList[0];
}

function isRootPage(path, webRoutes = []) {
  return (
    webRoutes.filter((webRoute) => {
      if (webRoute === '/') {
        return path === webRoute;
      }
      return path.startsWith(webRoute);
    }).length > 0
  );
}

function updateStorageFun(currentMilliseconds) {
  return function (val) {
    updateDialogCache(val.templateKey, currentMilliseconds);
    updateDialogStorage(val.templateKey, currentMilliseconds);
  };
}

function sortByPriority(filterList) {
  return filterList.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority > b.priority ? -1 : 0;
    }
    return a.createTime > b.createTime ? -1 : 0;
  });
}

// 新的一天清除多次弹出
function reInitialTemplateFatigueCount(dataList, now) {
  // 新的一天清除多次弹出次数, 不处理超时的template
  const nowDay = genDayNumber(now);
  const resetCountKey = dataList.reduce((p, item) => {
    if (item.fatigueType === 2) {
      const time = dialogCache[item.templateKey].time;
      const day = genDayNumber(time);
      if (time > 0 && Math.ceil(nowDay - day) > 0 && time + item.fatigueIntervalSeconds < now) {
        p.push(item.templateKey);
      }
    }
    return p;
  }, []);

  if (resetCountKey.length > 0) {
    const storeDialogCache = getDialogStorage();
    resetCountKey.forEach((templateKey) => {
      dialogCache[templateKey].count = 0;
      dialogCache[templateKey].time = now;

      storeDialogCache[templateKey].count = 0;
      storeDialogCache[templateKey].time = now;
    });
    updateAllStorage(storeDialogCache);
  }
}

function genDayNumber(time) {
  const day = timeUtil.formatDate(time);
  return Number(day.replace(/-/g, ''));
}

//更新缓存
function updateDialogCache(templateKey, currentMilliseconds) {
  if (dialogCache.hasOwnProperty(templateKey)) {
    const dialogCacheInfo = dialogCache[templateKey];
    dialogCache[templateKey].count = dialogCacheInfo.count + 1;
    dialogCache[templateKey].time = currentMilliseconds;
  }
}

// 取localstorage中dialog_count
function getDialogStorage() {
  if (env.isServer) {
    return {};
  }
  const dialogCountString = localStorage.getItem('dialog_count') || '';
  return dialogCountString.split(',').reduce((p, d) => {
    const [key, value] = d.split(':');
    if (key) {
      const [count, time] = (value || '').split('_');
      p[key] = {count: Number(count || 0), time: Number(time || 0)};
    }
    return p;
  }, {});
}

// 更新localstorage中的key对应value
function updateDialogStorage(key, time) {
  const template = dialogCache[key];
  const dialogStorage = getDialogStorage();
  dialogStorage[key] = {count: template.count, time};
  updateAllStorage(dialogStorage);
}

// 更新dialog_count中整个数据
function updateAllStorage(dialogStorage) {
  const dialogStr = Object.keys(dialogStorage)
    .map((key) => `${key}:${dialogStorage[key].count}_${dialogStorage[key].time}`)
    .join(',');
  localStorage.setItem('dialog_count', dialogStr);
}
